import {Spinner} from '@/components/ui/spinner/Spinner'
import {useMe} from '@/features/authentication/queries/useMe'
import {remapCurrency} from '@/features/profile/constants'
import {useFetchSubscribedAmounts} from '@/features/recurring-gift/queries/useFetchSubscribedAmounts'
import {DonationType, DonationV3} from '@/features/recurring-gift/types'
import {TFunction} from 'i18next'
import {FC, useEffect, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {PlanOfSubscription} from './components/plan-of-subscription/PlanOfSubscription'
import {StyledPlansList, StyledPlansListHeader} from './style'
import {useGetMembershipPlans} from '@/features/configure-membership-plans/edit-configure-membership-plans-modal/queries/useGetMembershipPlans'
import {MixpanelEventNameE} from '@/integrations/mixpanel/types'
import useMixPanel from '@/integrations/mixpanel/hooks/useMixpanel'
import membership from '@assets/images/new-icons/membership.png'
import {ToolTipActionsModal} from '@/features/profile/components/host-dashboard/actions-section/components/toolTipActionsModal/ToolTipActionsModal'
import {SubscriptionStatusesE} from '@/features/chat/chat.model'
import {useGetSubscribedHostsV3} from '@/features/recurring-gift/queries/useGetSubscribedHostsV3'

type PlansListProps = {
    selectedPlan: DonationType | null
    columnNames?: string[]
    disabledByMinAmount?: number
    hostID?: string
    onSelectPlanHandler: (plan: DonationType) => void
    renderMegaphoneText?: (
        plan: DonationType,
        numberOfOthersGroups: number,
        t?: TFunction<'translation', undefined>
    ) => React.ReactNode
}
export const PlansList: FC<PlansListProps> = ({
    selectedPlan,
    onSelectPlanHandler,
    renderMegaphoneText,
    disabledByMinAmount = 0,
    hostID,
    columnNames = ['configure_membership_plans_modal:plan_price', 'configure_membership_plans_modal:rewards']
}) => {
    const {t} = useTranslation()
    const mixpanel = useMixPanel()
    const {data: user} = useMe()
    const [showInfoModal, setShowInfoModal] = useState(false)
    const {data: subscriptionHosts} = useGetSubscribedHostsV3({
        enabled: true,
        status: SubscriptionStatusesE.ACTIVE
    })
    const activeSubscription = subscriptionHosts?.find(
        (subsriptionsHost: DonationV3) => subsriptionsHost.rookieId.toString() === `${hostID}`
    )

    const {data: packagesListForGuest, isLoading: isLoadingGuest} = useGetMembershipPlans(hostID)
    const {data: packagesListForHost, isLoading: isLoadingHost} = useFetchSubscribedAmounts(false, !hostID)
    const packagesList = hostID
        ? packagesListForGuest?.filter(pack => pack?.group?.status === 'active' && !!pack.group)
        : packagesListForHost?.filter(pack => pack.group.status === 'inactive' && pack)

    const filteredPackagesList = !activeSubscription
        ? packagesList?.sort((a, b) => Number(Boolean(b.discount)) - Number(Boolean(a.discount)))
        : packagesList?.sort(
              (a, b) =>
                  Number(
                      Boolean(b.discount) &&
                          !!activeSubscription &&
                          activeSubscription.amount +
                              (activeSubscription?.discountedAmount ? +activeSubscription?.discountedAmount : 0) ===
                              b.amount
                  ) -
                  Number(
                      Boolean(a.discount) &&
                          !!activeSubscription &&
                          activeSubscription.amount +
                              (activeSubscription?.discountedAmount ? +activeSubscription?.discountedAmount : 0) ===
                              a.amount
                  )
          )

    useEffect(() => {
        if (selectedPlan) {
            const updatedSelectedPlan = packagesList?.find(({id}) => id === selectedPlan.id)
            if (updatedSelectedPlan) onSelectPlanHandler(updatedSelectedPlan)
        }
    }, [packagesList])

    if (
        (hostID ? isLoadingGuest : isLoadingHost) ||
        (hostID ? !packagesListForGuest?.length : !packagesListForHost?.length)
    )
        return <Spinner />
    const currencySymbol = remapCurrency[user?.currency || 'USD']

    return (
        <>
            <StyledPlansListHeader>
                {columnNames.map((name: string) => (
                    <span key={name}>{t(name)}</span>
                ))}
            </StyledPlansListHeader>
            <StyledPlansList>
                {filteredPackagesList?.map((plan, idx) => {
                    return (
                        <PlanOfSubscription
                            key={plan.id}
                            plan={plan}
                            isSelected={plan.id === selectedPlan?.id}
                            isActive={
                                activeSubscription &&
                                activeSubscription.amount +
                                    (activeSubscription?.discountedAmount
                                        ? +activeSubscription?.discountedAmount
                                        : 0) ===
                                    plan?.amount
                            }
                            onSelectPlanHandler={() => {
                                mixpanel?.trackEvent(MixpanelEventNameE.GiftAmountClick, {
                                    amountSelected: plan.amount
                                })
                                onSelectPlanHandler(plan)
                            }}
                            onClickTooltip={() => {
                                setShowInfoModal(true)
                            }}
                            currencySymbol={currencySymbol}
                            renderMegaphoneText={() => renderMegaphoneText?.(plan, idx, t)}
                            disabled={!!disabledByMinAmount && disabledByMinAmount > plan.amount}
                            hasCoupon={
                                (Boolean(plan.discount) && !activeSubscription) ||
                                (Boolean(plan.discount) &&
                                    activeSubscription &&
                                    activeSubscription.amount +
                                        (activeSubscription?.discountedAmount
                                            ? +activeSubscription?.discountedAmount
                                            : 0) ===
                                        plan?.amount)
                            }
                        />
                    )
                })}
            </StyledPlansList>
            {showInfoModal && (
                <ToolTipActionsModal
                    content={{
                        title: t('configure_membership_plans_modal:info_modal:title'),
                        icon: () => <img src={membership} />,
                        content: 'gift_rookie:info_tooltip'
                    }}
                    onClose={() => setShowInfoModal(false)}
                />
            )}
        </>
    )
}
